import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { AppRoutes, DIGITS_AND_CHARS_ONLY_REGEX, LOGIN_SUBMIT_BUTTON_CLASSES } from '@/constants';
import { useCountDown, useResendOTP, useVerifyOTP } from '@/hooks';
import { IOTPVerificationPayload } from '@/interfaces';
import { navigate } from '@/providers';
import { getApiErrorMessage } from '@/utils';

import {
  AuthGlassContainer,
  Button,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/common';
import { Alert, handleServerValidationErrors } from '@/lib/ui';

import {
  DEFAULT_MAX_INVALID_OTP_ATTEMPTS,
  DEFAULT_MAX_RESEND_ATTEMPTS,
  DEFAULT_OTP_EXPIRY_SECONDS,
  DEFAULT_OTP_LENGTH,
  DEFAULT_RESEND_OTP_EXPIRY_SECONDS,
  OTP_FORM_FIELDS,
  OTP_SCHEMA,
} from '@/features/auth/constants';

import { ExpiredPasswordUpdate } from './ExpiredPasswordUpdate';

interface IOTPVerification {
  email: string;
  onBack: () => void;
}

export const OTPVerification = ({ email, onBack }: IOTPVerification) => {
  const OTP_EXPIRY_SECONDS =
    Number(process.env.REACT_APP_OTP_EXPIRY_SECONDS) || DEFAULT_OTP_EXPIRY_SECONDS;
  const RESEND_OTP_EXPIRY_SECONDS =
    Number(process.env.REACT_APP_RESEND_OTP_EXPIRY_SECONDS) || DEFAULT_RESEND_OTP_EXPIRY_SECONDS;
  const OTP_LENGTH = Number(process.env.REACT_APP_OTP_LENGTH) || DEFAULT_OTP_LENGTH;
  const MAX_RESEND_ATTEMPTS =
    process.env.REACT_APP_MAX_RESEND_ATTEMPTS || DEFAULT_MAX_RESEND_ATTEMPTS;
  const MAX_INVALID_OTP_ATTEMPTS =
    process.env.REACT_APP_MAX_INVALID_OTP_ATTEMPTS || DEFAULT_MAX_INVALID_OTP_ATTEMPTS;

  const [resendAttempts, setResendAttempts] = useState(0);
  const [hasUserReachedOTPResendLimit, setHasUserReachedOTPResendLimit] = useState(false);
  const [invalidOTPAttempts, setInvalidOTPAttempts] = useState(0);
  const [showExpiredPasswordUpdateForm, setShowExpiredPasswordUpdateForm] = useState(false);

  const { mutateAsync: resendOTPMutationAsync, isPending: isResendOTPPending } = useResendOTP();
  const { mutateAsync: verifyOTPMutationAsync, isPending: isVerifyOTPPending } = useVerifyOTP();

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    watch,
    trigger,
    formState: { errors },
  } = useForm<IOTPVerificationPayload>({
    resolver: yupResolver(OTP_SCHEMA),
    values: { email: email, otp: '' },
  });

  const {
    isExpired: isOTPExpired,
    formattedTime: otpTime,
    reset: resetOTPCountdown,
  } = useCountDown({
    initialSeconds: OTP_EXPIRY_SECONDS,
  });

  const {
    formattedTime: resendOTPTime,
    reset: resetResendOTPCountdown,
    isRunning: isResendOTPRunning,
  } = useCountDown({
    initialSeconds: RESEND_OTP_EXPIRY_SECONDS,
  });

  const onSubmit = async ({ otp }) => {
    try {
      await verifyOTPMutationAsync(
        {
          email,
          otp,
        },
        {
          onSuccess: (data) => {
            if (data.isPasswordExpired) {
              setShowExpiredPasswordUpdateForm(true);
            } else {
              navigate(AppRoutes.HOME);
            }
          },
        },
      );
    } catch (error) {
      const apiMessage = getApiErrorMessage(error);
      if (apiMessage) {
        setError(OTP_FORM_FIELDS.otp, { type: 'manual', message: apiMessage });
      }
      setInvalidOTPAttempts(invalidOTPAttempts + 1);
    }
  };

  const onResendOTP = () => {
    setResendAttempts(0);
    resendOTPMutationAsync(email)
      .then(() => {
        resetOTPCountdown();
        resetResendOTPCountdown();

        const newAttempts = resendAttempts + 1;
        setResendAttempts(newAttempts);

        if (newAttempts >= Number(MAX_RESEND_ATTEMPTS)) {
          setHasUserReachedOTPResendLimit(true);
        }

        if (errors[OTP_FORM_FIELDS.otp]) clearErrors(OTP_FORM_FIELDS.otp);
        Alert.success('New OTP code has been sent to your email.');
      })
      .catch((error) => {
        handleServerValidationErrors(error, setError);
      });
  };

  useEffect(() => {
    if (email) {
      resetOTPCountdown();
    }
  }, [email, resetOTPCountdown]);

  useEffect(() => {
    if (invalidOTPAttempts >= Number(MAX_INVALID_OTP_ATTEMPTS)) {
      Alert.error('Too many invalid OTP attempts. Please sign in again and request a new OTP.');
      onBack();
    }
  }, [invalidOTPAttempts, MAX_INVALID_OTP_ATTEMPTS, onBack]);

  if (!email) {
    navigate(AppRoutes.NOT_FOUND);
    return null;
  }

  if (showExpiredPasswordUpdateForm) {
    return (
      <ExpiredPasswordUpdate
        email={email}
        otp={watch(OTP_FORM_FIELDS.otp)}
        onSuccess={() => {
          setShowExpiredPasswordUpdateForm(false);
          onBack();
        }}
      />
    );
  }

  return (
    <AuthGlassContainer className="p-8 w-[400px]">
      {!isVerifyOTPPending ? (
        <div className="flex flex-col gap-8 [&_*]:items-center [&_*]:text-center">
          <div className="flex flex-col gap-6">
            <div className="text-h6 text-neutral-0 font-bold">OTP VERIFICATION</div>
            <div className="flex flex-col gap-5">
              <div className="text-body-sm text-neutral-0 font-semibold">
                We have sent an email with your OTP code to {email}
              </div>
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="otp"
                  render={({ field }) => (
                    <InputOTP
                      label="Please enter OTP code"
                      error={errors[OTP_FORM_FIELDS.otp]?.message}
                      length={OTP_LENGTH}
                      pattern={DIGITS_AND_CHARS_ONLY_REGEX.source}
                      value={field.value}
                      onChange={(newOTP) => {
                        field.onChange(newOTP);
                        trigger(OTP_FORM_FIELDS.otp);
                      }}
                    >
                      <InputOTPGroup>
                        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                          <InputOTPSlot
                            key={index}
                            index={index}
                            isError={!!errors[OTP_FORM_FIELDS.otp]}
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
                {!isOTPExpired && (
                  <div className="text-body-sm text-opacity-light-70 font-regular">
                    OTP code will be expired in {otpTime}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      className={
                        isResendOTPPending
                          ? 'disabled:!text-opacity-light-50'
                          : 'disabled:!text-opacity-light-30'
                      }
                      buttonColor="primary"
                      buttonStyle="textlink"
                      borderRadius="soft"
                      size="lg"
                      onClick={onResendOTP}
                      prefixIcon={
                        isResendOTPPending ? (
                          <span className="!size-4 shrink-0 border-[2px] border-opacity-light-50 border-b-transparent rounded-full inline-block box-border animate-spin direction-reverse"></span>
                        ) : undefined
                      }
                      disabled={
                        isResendOTPRunning || hasUserReachedOTPResendLimit || isResendOTPPending
                      }
                    >
                      {isResendOTPPending ? 'Resending OTP...' : 'Resend OTP'}
                    </Button>
                    {isResendOTPRunning && !hasUserReachedOTPResendLimit && (
                      <span className="text-opacity-light-70 font-regular text-body-sm">
                        ({resendOTPTime})
                      </span>
                    )}
                  </div>
                  {hasUserReachedOTPResendLimit && (
                    <div className="text-body-sm font-medium text-semantic-error-base">
                      You have reached the OTP resend limit. Sign in again to request a new
                      verification code.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <Button
              buttonColor="primary"
              buttonStyle="filled"
              size="lg"
              className={LOGIN_SUBMIT_BUTTON_CLASSES}
              onClick={handleSubmit(onSubmit)}
              disabled={isVerifyOTPPending || watch(OTP_FORM_FIELDS.otp).length !== OTP_LENGTH}
            >
              Verify
            </Button>
            <div>
              <Button
                buttonColor="neutral"
                buttonStyle="textlink"
                borderRadius="soft"
                size="lg"
                onClick={onBack}
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6 items-center">
          <span className="w-10 h-10 shrink-0 border-4 border-neutral-0 border-b-transparent rounded-full inline-block box-border animate-spin"></span>
          <span className="font-semibold text-body-base text-neutral-0">
            Please wait a moment while we log you in…
          </span>
        </div>
      )}
    </AuthGlassContainer>
  );
};
